import { Card as MuiCard } from '@mui/material';
import { colorsFoundation } from '../tokens/colors-foundation.js';
import { dimensions, borderRadius } from '../tokens/dimensions.js';
import { hexToRgba } from '../utils/colors.js';

/**
 * Card component - container with background, border radius, padding, and shadow
 * Based on Figma design system, wraps MUI Card
 * 
 * Variants:
 * - default: Standard card with white background
 * - elevated: Card with shadow and elevation
 * - auth: Auth-specific card with soft gray background and larger padding
 * - glass: Semi-transparent card with backdrop blur (for overlays)
 * 
 * @param {'default'|'elevated'|'auth'|'glass'} props.variant - Card style variant
 * @param {React.ReactNode} props.children - Card content
 * @param {object} props.sx - Additional MUI sx styles
 */
export default function Card
({
    variant = 'default',
    children,
    sx = {},
    ...props
})
{
    const variantStyles = {
        default: {
            bgcolor: colorsFoundation.neutral.white,
            borderRadius: borderRadius.md,
            padding: dimensions['2xl'],
            boxShadow: 'none'
        },
        elevated: {
            bgcolor: colorsFoundation.neutral.white,
            borderRadius: borderRadius.lg,
            padding: dimensions['3xl'],
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
        },
        auth: {
            bgcolor: colorsFoundation.neutral[50],
            borderRadius: '10px',
            padding: dimensions['4xl'],
            boxShadow: '0 36px 80px rgba(0, 0, 0, 0.08)'
        },
        glass: {
            bgcolor: hexToRgba(colorsFoundation.neutral[100], 0.98),
            borderRadius: borderRadius.lg,
            padding: dimensions['5xl'],
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)' // Safari support
        }
    };

    return (
        <MuiCard
            elevation={0}
            sx={{
                ...variantStyles[variant],
                ...sx
            }}
            {...props}
        >
            {children}
        </MuiCard>
    );
}
